import { Card, Tabs, Row, Col, Statistic, Divider, Typography, InputNumber, Form, Switch, Button, message, TimePicker, Tooltip } from 'antd';
import { DollarOutlined, ThunderboltOutlined, MoonOutlined, SettingOutlined, MailOutlined, InfoCircleOutlined, ExperimentOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api-client';
import { RoutePricingList } from './RoutePricingList';
import { RouteSurgeManager } from './RouteSurgeManager';
import { PostagePricingList } from './PostagePricingList';
import { DerivedPricingTest } from './DerivedPricingTest';
import { LabelWithHelp } from '../../components/ui/HelpTooltip';
import dayjs from 'dayjs';
import { t } from '../../i18n';

const { Text } = Typography;

// Simple section title component
const SectionTitleSimple = ({ children, icon }: { children: React.ReactNode; icon?: React.ReactNode }) => (
  <h4 style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
    {icon}
    {children}
  </h4>
);

interface PricingSettings {
  // Base pricing
  defaultPricePerKm: number;

  // Surge pricing
  surgePricingEnabled: boolean;
  surgeThresholdRatio: number;
  surgeMinMultiplier: number;
  surgeMaxMultiplier: number;

  // Night pricing
  nightPricingEnabled: boolean;
  nightStartHour: number;
  nightEndHour: number;
  nightPriceMultiplier: number;
}

export const PricingPage = () => {
  const [settings, setSettings] = useState<PricingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get('/admin/settings');
      setSettings(response.data);
      if (response.data) {
        form.setFieldsValue({
          ...response.data,
          nightStartTime: dayjs().hour(response.data.nightStartHour || 22).minute(0),
          nightEndTime: dayjs().hour(response.data.nightEndHour || 6).minute(0),
        });
      }
    } catch {
      message.error('Sozlamalarni yuklashda xatolik');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values: any) => {
    setSaving(true);
    try {
      const data = {
        defaultPricePerKm: values.defaultPricePerKm,
        surgePricingEnabled: values.surgePricingEnabled,
        surgeThresholdRatio: values.surgeThresholdRatio,
        surgeMinMultiplier: values.surgeMinMultiplier,
        surgeMaxMultiplier: values.surgeMaxMultiplier,
        nightPricingEnabled: values.nightPricingEnabled,
        nightStartHour: values.nightStartTime?.hour() || 22,
        nightEndHour: values.nightEndTime?.hour() || 6,
        nightPriceMultiplier: values.nightPriceMultiplier,
      };
      await apiClient.put('/admin/settings', data);
      message.success(t('settings.saved'));
      fetchSettings();
    } catch {
      message.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const tabItems = [
    {
      key: 'routes',
      label: (
        <span>
          <DollarOutlined /> {t('pricing.routesTab')}
        </span>
      ),
      children: <RoutePricingList />,
    },
    {
      key: 'postage',
      label: (
        <span>
          <MailOutlined /> {t('pricing.postageTab')}
        </span>
      ),
      children: <PostagePricingList />,
    },
    {
      key: 'test',
      label: (
        <span>
          <ExperimentOutlined /> Test
        </span>
      ),
      children: <DerivedPricingTest />,
    },
    {
      key: 'surge',
      label: (
        <span>
          <ThunderboltOutlined /> {t('pricing.surgeTab')}
        </span>
      ),
      children: <RouteSurgeManager />,
    },
    {
      key: 'settings',
      label: (
        <span>
          <SettingOutlined /> {t('pricing.settingsTab')}
        </span>
      ),
      children: (
        <Card loading={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            initialValues={{
              surgePricingEnabled: true,
              nightPricingEnabled: true,
            }}
          >
            {/* Base Pricing */}
            <SectionTitleSimple>{t('pricing.basePricing')}</SectionTitleSimple>
            <Row gutter={24}>
              <Col span={8}>
                <Form.Item
                  name="defaultPricePerKm"
                  label={
                    <LabelWithHelp
                      label="Km uchun narx (so'm)"
                      help="Agar yo'nalish uchun maxsus narx belgilanmagan bo'lsa ishlatiladi"
                    />
                  }
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                    parser={(value) => value!.replace(/\s?/g, '')}
                    min={0}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            {/* Surge Pricing */}
            <SectionTitleSimple icon={<ThunderboltOutlined />}>{t('settings.surgePricing')}</SectionTitleSimple>
            <Row gutter={24}>
              <Col span={8}>
                <Form.Item
                  name="surgePricingEnabled"
                  label={
                    <LabelWithHelp
                      label="Surge narxlash"
                      help="Yuqori talab vaqtlarida avtomatik narx oshirish"
                    />
                  }
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Yoqilgan" unCheckedChildren="O'chirilgan" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="surgeThresholdRatio"
                  label={
                    <LabelWithHelp
                      label="Boshlash nisbati"
                      help="So'rovlar/Haydovchilar nisbati bu qiymatdan oshganda surge boshlanadi"
                    />
                  }
                >
                  <InputNumber style={{ width: '100%' }} min={1} max={10} step={0.1} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={8}>
                <Form.Item
                  name="surgeMinMultiplier"
                  label={
                    <LabelWithHelp
                      label="Minimal multiplikator"
                      help="Eng kam narx ko'paytiruvchi (masalan, 1.1 = 10% qo'shimcha)"
                    />
                  }
                >
                  <InputNumber style={{ width: '100%' }} min={1} max={5} step={0.1} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="surgeMaxMultiplier"
                  label={
                    <LabelWithHelp
                      label="Maksimal multiplikator"
                      help="Eng ko'p narx ko'paytiruvchi (masalan, 2.0 = 100% qo'shimcha)"
                    />
                  }
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const min = getFieldValue('surgeMinMultiplier');
                        if (value != null && min != null && value < min) {
                          return Promise.reject('Maksimal multiplikator minimalniydan kichik bo\'lmasligi kerak');
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <InputNumber style={{ width: '100%' }} min={1} max={5} step={0.1} />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            {/* Night Pricing */}
            <SectionTitleSimple icon={<MoonOutlined />}>{t('settings.nightPricing')}</SectionTitleSimple>
            <Row gutter={24}>
              <Col span={8}>
                <Form.Item
                  name="nightPricingEnabled"
                  label={
                    <LabelWithHelp
                      label="Tungi narxlash"
                      help="Kechasi narxlarni avtomatik oshirish"
                    />
                  }
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Yoqilgan" unCheckedChildren="O'chirilgan" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="nightPriceMultiplier"
                  label={
                    <LabelWithHelp
                      label="Tungi multiplikator"
                      help="Tungi narx ko'paytiruvchi (masalan, 1.2 = 20% qo'shimcha)"
                    />
                  }
                >
                  <InputNumber style={{ width: '100%' }} min={1} max={3} step={0.1} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={8}>
                <Form.Item
                  name="nightStartTime"
                  label={
                    <LabelWithHelp
                      label="Tun boshlanishi"
                      help="Tungi narxlash boshlanadigan vaqt"
                    />
                  }
                >
                  <TimePicker format="HH:mm" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="nightEndTime"
                  label={
                    <LabelWithHelp
                      label="Tun tugashi"
                      help="Tungi narxlash tugaydigan vaqt"
                    />
                  }
                >
                  <TimePicker format="HH:mm" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8} style={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title="Masalan: 22:00 dan 06:00 gacha. Boshlanish vaqti tugash vaqtidan katta bo'lishi mumkin (yarim tunni o'z ichiga oladi).">
                  <InfoCircleOutlined style={{ color: '#7F8C8D', fontSize: 16 }} />
                </Tooltip>
              </Col>
            </Row>

            <Divider />

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={saving}>
                Saqlash
              </Button>
            </Form.Item>
          </Form>
        </Card>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 24 }}>
        <DollarOutlined style={{ marginRight: 8 }} />
        {t('pricing.title')}
      </h2>
      <Tabs items={tabItems} defaultActiveKey="routes" />
    </div>
  );
};
